import React from "react";
import {
  Box,
  Paper,
  Button,
  CircularProgress,
  Typography,
  Grid,
  Chip,
  IconButton,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import AddIcon from "@mui/icons-material/Add";

const MediaUploadSection = ({
  contentType,
  isUploading,
  platforms,
  selectedAccounts,
  handleMediaUpload,
  mediaFile,
  removeMediaFile,
  removeAllMediaFiles,
  setSelectedImage,
  setImageModalOpen,
}) => {
  const disabledUpload =
    isUploading ||
    !contentType ||
    platforms.length === 0 ||
    selectedAccounts.length === 0;

  return (
    <>
      {!mediaFile || (Array.isArray(mediaFile) && mediaFile.length === 0) ? (
        <Button
          component="label"
          variant="outlined"
          startIcon={
            isUploading ? <CircularProgress size={16} /> : <CloudUploadIcon />
          }
          disabled={disabledUpload}
          sx={{
            bgcolor: "#fff",
            color: "#4B5AB6",
            borderColor: "#4B5AB6",
            fontWeight: 700,
            borderRadius: 2,
            py: 1.5,
            px: 3,
            "&:hover": { bgcolor: "#eef2ff" },
            "&:disabled": {
              bgcolor: "#f1f5f9",
              color: "#94a3b8",
              borderColor: "#cbd5e1",
            },
          }}
        >
          {isUploading ? "Uploading..." : `Upload ${contentType}`}
          {!isUploading &&
            (contentType === "Image" ? " (Max: 10)" : " (Max: 5)")}
          <input
            type="file"
            accept={contentType === "Image" ? "image/*" : "video/*"}
            multiple
            hidden
            onChange={handleMediaUpload}
            disabled={isUploading}
          />
        </Button>
      ) : (
        <Paper
          sx={{
            p: 3,
            bgcolor: "#f8fafc",
            borderRadius: 3,
            border: "2px dashed #d1d5db",
            position: "relative",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#1e293b" }}>
              {contentType} Files (
              {Array.isArray(mediaFile) ? mediaFile.length : 1})
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                component="label"
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                disabled={
                  (contentType === "Image" &&
                    Array.isArray(mediaFile) &&
                    mediaFile.length >= 10) ||
                  (contentType === "Video" &&
                    Array.isArray(mediaFile) &&
                    mediaFile.length >= 5)
                }
                sx={{
                  color: "#4B5AB6",
                  borderColor: "#4B5AB6",
                  fontWeight: 600,
                  "&:hover": { bgcolor: "#eef2ff" },
                }}
              >
                Add More
                <input
                  type="file"
                  accept={contentType === "Image" ? "image/*" : "video/*"}
                  multiple
                  hidden
                  onChange={handleMediaUpload}
                />
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={removeAllMediaFiles}
                sx={{
                  color: "#ef4444",
                  borderColor: "#ef4444",
                  fontWeight: 600,
                  "&:hover": {
                    bgcolor: "#fef2f2",
                    borderColor: "#dc2626",
                    color: "#dc2626",
                  },
                }}
              >
                Remove All
              </Button>
            </Box>
          </Box>

          <Grid container spacing={2}>
            {(Array.isArray(mediaFile) ? mediaFile : [mediaFile]).map(
              (file, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Paper
                    sx={{
                      p: 2,
                      bgcolor: "#fff",
                      borderRadius: 2,
                      border: "1px solid #e2e8f0",
                      position: "relative",
                      "&:hover": { boxShadow: "0 4px 12px rgba(0,0,0,0.1)" },
                    }}
                  >
                    <IconButton
                      onClick={() => removeMediaFile(index)}
                      sx={{
                        position: "absolute",
                        top: 4,
                        right: 4,
                        bgcolor: "#ef4444",
                        color: "#fff",
                        zIndex: 1,
                        width: 24,
                        height: 24,
                        "&:hover": { bgcolor: "#dc2626" },
                      }}
                      size="small"
                      title="Remove this file"
                    >
                      ✕
                    </IconButton>

                    <Box
                      onClick={() => {
                        setSelectedImage(file.s3Url || file); // File fallback
                        setImageModalOpen(true);
                      }}
                      sx={{ mb: 2, cursor: "pointer" }}
                    >
                      {contentType === "Image" ? (
                        <Box
                          sx={{
                            width: "100%",
                            height: 120,
                            borderRadius: 2,
                            overflow: "hidden",
                            bgcolor: "#e5e7eb",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <img
                            src={URL.createObjectURL(file)}
                            alt={`Preview ${index + 1}`}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            width: "100%",
                            borderRadius: 2,
                            overflow: "hidden",
                            bgcolor: "#e5e7eb",
                          }}
                        >
                          <video
                            src={URL.createObjectURL(file)}
                            controls
                            style={{
                              width: "100%",
                              maxHeight: "120px",
                              borderRadius: "8px",
                            }}
                          />
                        </Box>
                      )}
                    </Box>

                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: "#1e293b",
                          wordBreak: "break-word",
                          fontSize: "0.8rem",
                          mb: 1,
                        }}
                      >
                        {file.name.length > 20
                          ? `${file.name.substring(0, 20)}...`
                          : file.name}
                      </Typography>

                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography variant="caption" sx={{ color: "#6b7280" }}>
                          {(file.size / 1024 / 1024).toFixed(1)} MB
                        </Typography>
                        <Chip
                          label={file.type.split("/")[1].toUpperCase()}
                          size="small"
                          sx={{
                            bgcolor: "#dbeafe",
                            color: "#1e40af",
                            fontSize: "0.6rem",
                            height: 20,
                          }}
                        />
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              )
            )}
          </Grid>

          <Box
            sx={{
              mt: 3,
              p: 2,
              bgcolor: "#fffbeb",
              borderRadius: 2,
              border: "1px solid #fbbf24",
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: "#92400e", fontWeight: 600 }}
            >
              Upload Limits:
            </Typography>
            <Typography variant="caption" sx={{ color: "#92400e", ml: 1 }}>
              {contentType === "Image"
                ? `${
                    Array.isArray(mediaFile) ? mediaFile.length : 1
                  }/10 images • Max 10MB each`
                : `${
                    Array.isArray(mediaFile) ? mediaFile.length : 1
                  }/5 videos • Max 50MB each`}
            </Typography>
          </Box>
        </Paper>
      )}

      {contentType && (
        <Typography
          variant="caption"
          sx={{
            color: "#6b7280",
            mt: 1,
            display: "block",
            fontStyle: "italic",
          }}
        >
          Supported formats:{" "}
          {contentType === "Image"
            ? "JPG, PNG, GIF, WebP • Max: 10 files, 10MB each"
            : "MP4, MOV, AVI, WebM • Max: 5 files, 50MB each"}
        </Typography>
      )}
    </>
  );
};

export default MediaUploadSection;
